import {
  DrawerButtonRow,
  EngineReactComponentsContext,
  TreeReferencesContextProvider,
  WidgetConfigForm,
  WidgetRepository,
  useConfigValidationContext,
  useEngineTheme,
  useWidgetRootContext,
} from '@framework/engine';
import { FormHandler } from '@framework/form';
import { Drawer } from '@framework/ui';
import { yup } from '@framework/validation';
import { Button, Space, message } from 'antd';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SortableWidgetEditor } from '../dnd/SortableWidgetEditor';
import { useDndEditorContext } from '../dnd/hooks/useDndEditorContext';

const DrawerContentWrapper = styled.div`
  // impostiamo delle classi generali per impostare il colore dell'indagine
  // per il drawer che viene renderizzato fuori dalla "pagina" nella root
  // quando non è possibili impostare il colore direttamente sul componente
  // (es. testi html del campo Rte)
  .survey-color {
    color: ${props => props.theme.engine.accentColor ?? 'inherit'};
  }
  .survey-background-color {
    background-color: ${props => props.theme.engine.accentColor ?? 'inherit'};
  }
  .survey-background-color {
    background-color: ${props => props.theme.engine.accentColor ?? 'inherit'};
  }
`;

export interface WidgetConfigDrawerProps {}

export function WidgetConfigDrawer(props: WidgetConfigDrawerProps) {
  const { configEditor } = useDndEditorContext();
  const { root } = useWidgetRootContext();
  const theme = useEngineTheme();

  const widgetType = WidgetRepository.find(configEditor.selected?.type);
  const validator = useMemo(
    () =>
      WidgetRepository.getConfigValidationSchema(configEditor.selected) ??
      defaultValidationSchema,
    [configEditor.selected],
  );

  const validationContext = useConfigValidationContext({ isDraft: true });

  const widgetEditorComponent = useMemo(
    () => ({ sortableWidgetEditor: SortableWidgetEditor }),
    [],
  );

  return (
    <Drawer
      title={`Configurazione Elemento ${widgetType?.name ?? ''}`}
      size="large"
      width={widgetType?.drawerWidth}
      placement="right"
      onClose={() => configEditor.closeConfig()}
      open={!!configEditor.selected}
      destroyOnClose
    >
      <TreeReferencesContextProvider
        root={root!}
        widget={configEditor.selected!}
      >
        <EngineReactComponentsContext.Provider value={widgetEditorComponent}>
          <FormHandler
            nested
            hideVisualFeedback
            layout="vertical"
            schema={validator}
            initialValues={validator.cast(configEditor.selected, {
              assert: false,
            })}
            validationContext={validationContext}
            onSubmit={async (rawValues, e) => {
              try {
                const values = await validator.validate(rawValues, {
                  context: validationContext,
                });

                if (process.env.NODE_ENV !== 'production') console.log(`[Widget]`, values); // prettier-ignore

                configEditor.apply({
                  ...configEditor.selected,
                  ...values,
                });
                configEditor.closeConfig();
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                message.success(`Elemento aggiornato`);
              } catch (e) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                message.error('Si è verificato un errore nel salvataggio della configurazione'); // prettier-ignore
                console.error(e);
              }
            }}
          >
            {configEditor.selected && widgetType && (
              <DrawerContentWrapper color={theme.accentColor ?? undefined}>
                <WidgetConfigForm
                  widgetType={widgetType}
                  widget={configEditor.selected}
                />
                <DrawerButtonRow>
                  <Space>
                    <Button
                      size="large"
                      onClick={() => configEditor.closeConfig()}
                    >
                      Annulla
                    </Button>
                    <Button type="primary" htmlType="submit" size="large">
                      Applica
                    </Button>
                  </Space>
                </DrawerButtonRow>
              </DrawerContentWrapper>
            )}
          </FormHandler>
        </EngineReactComponentsContext.Provider>
      </TreeReferencesContextProvider>
    </Drawer>
  );
}

const defaultValidationSchema = yup
  .object()
  .noUnknown() as yup.ObjectSchema<any>;
